import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'pithias-language';
const DEFAULT_LANGUAGE = 'ja';

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  isEnglish: false,
  setLanguage: () => {},
  toggleLanguage: () => {},
  text: (ja) => ja,
});

const readInitialLanguage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'en' ? 'en' : DEFAULT_LANGUAGE;
  } catch (_) {
    return DEFAULT_LANGUAGE;
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(readInitialLanguage);

  const setLanguage = useCallback((nextLanguage) => {
    const normalized = nextLanguage === 'en' ? 'en' : 'ja';
    setLanguageState(normalized);
    try {
      localStorage.setItem(STORAGE_KEY, normalized);
    } catch (_) {
      // The UI can still change language when storage is unavailable.
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'ja' ? 'en' : 'ja');
  }, [language, setLanguage]);

  const text = useCallback((ja, en) => (language === 'en' ? (en || ja) : ja), [language]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo(() => ({
    language,
    isEnglish: language === 'en',
    setLanguage,
    toggleLanguage,
    text,
  }), [language, setLanguage, text, toggleLanguage]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);

export const withLanguage = (Component) => {
  const Wrapped = (props) => {
    const language = useLanguage();
    return <Component {...props} {...language} languageContext={language} />;
  };
  Wrapped.displayName = `withLanguage(${Component.displayName || Component.name || 'Component'})`;
  return Wrapped;
};

export default LanguageContext;
